import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { TITRE_VERROU_BETA } from './CompteBetaAvis';

interface Rattachement {
  fournisseur: string;
  libelle: string;
  email: string | null;
  creeLe: string;
  utiliseLe: string | null;
}

interface MoyensConnexion {
  motDePasse: boolean;
  rattachements: Rattachement[];
}

const date = (iso: string) => new Date(iso).toLocaleDateString('fr-FR');

/**
 * « Moyens de connexion » de Mon compte (27/09/2026) : le mot de passe, et
 * chaque compte Google, Microsoft ou Apple rattaché — avec de quoi le retirer.
 *
 * Section masquée tant qu'aucun fournisseur n'est rattaché : sans lui, il n'y
 * a rien à voir ni à retirer. Le serveur refuse de retirer le DERNIER moyen de
 * connexion, et le dit.
 */
export function MoyensConnexionSection({ verrouille = false }: { verrouille?: boolean } = {}) {
  const [moyens, setMoyens] = useState<MoyensConnexion | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState<string | null>(null);

  const charger = useCallback(() => {
    api
      .get<MoyensConnexion>('/compte/rattachements')
      .then(setMoyens)
      .catch(() => setMoyens(null));
  }, []);

  useEffect(charger, [charger]);

  async function retirer(r: Rattachement) {
    if (!window.confirm(`Retirer la connexion avec ${r.libelle} ? Vous ne pourrez plus ouvrir votre compte par ${r.libelle}.`)) {
      return;
    }
    setOccupe(r.fournisseur);
    setErreur(null);
    try {
      await api.delete(`/compte/rattachements/${encodeURIComponent(r.fournisseur)}`);
      charger();
    } catch (err) {
      setErreur(err instanceof ApiError ? err.message : 'Retrait impossible pour le moment.');
    } finally {
      setOccupe(null);
    }
  }

  if (!moyens || moyens.rattachements.length === 0) return null;

  return (
    <>
      <h2 style={{ fontSize: '1rem', margin: '0 0 0.5rem' }}>Moyens de connexion</h2>
      {erreur ? <div className="alert alert-error">{erreur}</div> : null}
      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.5rem', fontSize: '0.9rem' }}>
        <li style={{ padding: '0.6rem 0', borderBottom: '1px solid var(--color-border)' }}>
          <strong>Mot de passe</strong>
          <div style={{ color: 'var(--text-color-light)', fontSize: '0.8rem' }}>
            {moyens.motDePasse ? 'défini' : 'aucun — « Mot de passe oublié » permet d’en définir un'}
          </div>
        </li>
        {moyens.rattachements.map((r) => (
          <li
            key={r.fournisseur}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1rem',
              padding: '0.6rem 0',
              borderBottom: '1px solid var(--color-border)',
            }}
          >
            <span>
              <strong>{r.libelle}</strong>
              <div style={{ color: 'var(--text-color-light)', fontSize: '0.8rem' }}>
                {r.email ? `${r.email} — ` : ''}ajouté le {date(r.creeLe)}
                {r.utiliseLe ? `, dernière connexion le ${date(r.utiliseLe)}` : ''}
              </div>
            </span>
            <button
              className="btn btn-secondary"
              style={{ width: 'auto', padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
              type="button"
              disabled={occupe !== null || verrouille}
              title={verrouille ? TITRE_VERROU_BETA : undefined}
              onClick={() => void retirer(r)}
            >
              Retirer
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}
